<template>
  <!-- Медиа управление -->
  <div
    ref="rootEl"
    class="col-12 col-md-5 m-auto d-flex align-items-center justify-content-between"
  >
    <button
      class="btn btn-secondary bg-secondary bg-opacity-05 border-0"
      @click="fastForward(-10 * 60)"
    >
      <img :src="$getImage('icons8-replay-10-48.png', 'icons')" class="col-10" alt="" />
    </button>
    <button
      class="btn btn-secondary bg-secondary bg-opacity-05 border-0"
      @click="fastForward(-5)"
    >
      <img :src="$getImage('icons8-replay-5-48.png', 'icons')" class="col-10" alt="" />
    </button>
    <button
      class="btn btn-secondary bg-secondary bg-opacity-05 border-0 fs-1"
      @click="togglePlay"
      style="width: 72px; height: 72px"
    >
      <i class="bi bi-play-fill" v-if="!play"></i>
      <i class="bi bi-pause-fill" v-else></i>
    </button>
    <button
      class="btn btn-secondary bg-secondary bg-opacity-05 border-0"
      @click="fastForward(5)"
    >
      <img :src="$getImage('icons8-forward-5-48.png', 'icons')" class="col-10" alt="" />
    </button>
    <button
      class="btn btn-secondary bg-secondary bg-opacity-05 border-0"
      @click="fastForward(10 * 60)"
    >
      <img :src="$getImage('icons8-forward-10-48.png', 'icons')" class="col-10" alt="" />
    </button>
  </div>

  <div
    v-if="playbackLabel"
    class="col-12 mt-2 px-3 text-center small text-white-50 text-truncate"
  >
    {{ playbackLabel }}
  </div>

  <BRange
    v-model="rangeValue"
    @dragStart="onDragStart"
    @rangeCommit="onRangeCommit"
    @dragEnd="onDragEnd"
    class="mt-3"
  />
</template>

<script lang="ts">
import { Options, Vue } from "vue-class-component";
import { useVRoom } from "@/store/modules/hotel/virtual-room";
import { useTVConnect } from "@/store/modules/media/tv-connect";

import BRange from "@/components/app/_shared/input/b-range.vue";

import type { VRAudiobooksStatus, VRCinemaStatus } from "@/@types/vroom/virtualRooma";
import type { TVPlaybackStatus } from "@/@types/connection/tvs";

/** Минимальный шаг обновления локальных часов (мс): ~30 к/с —
 *  ползунок идёт плавно, а не тиками раз в 250 мс */
const CLOCK_MIN_STEP_MS = 32;
/** Максимальный ход локальных часов без свежих статусов от ТВ (сек) */
const MAX_DRIFT_SEC = 5;
/** Допуск на устаревшие пакеты: меньший шаг назад не считается откатом */
const STALE_TOLERANCE_SEC = 1.5;
/** Окно ожидания подтверждения перемотки от ТВ (мс) */
const SEEK_GRACE_MS = 3000;
/** Подтверждённой перемоткой считается позиция в этих пределах (сек) */
const SEEK_CONFIRM_SEC = 2.5;

@Options({
  components: { BRange },
  emits: ["movieControl", "changeVolume"],
  watch: {
    activeCinema: {
      handler(this: MagicRemoteMultimedia) {
        // Пока на ТВ актуален аудиотрек — слайдер ведёт статус трека (tvPlayback)
        if (this.tvPlayback) return;
        if (this.isSeekPending) return;
        if (this.activeCinema?.current_time && this.activeCinema?.duration) {
          this.rangeValue =
            (100 * parseInt(this.activeCinema.current_time)) /
            parseInt(this.activeCinema.duration);
        } else this.rangeValue = 0;
      },
    },
    tvPlayback: {
      handler(this: MagicRemoteMultimedia) {
        this.applyPlayback(this.tvPlayback);
      },
    },
  },
})
class MagicRemoteMultimedia extends Vue {
  volume = 50;
  /** true — на ТВ идёт воспроизведение (кнопка показывает "пауза") */
  play = false;
  rangeValue = 0;

  pressedButton: HTMLElement | null = null;

  // --- Локальные часы сглаженного аудиопрогресса ---
  /** Отображаемые секунды (плавно идём между пакетами tv-status) */
  clockTime = 0;
  /** Последняя принятая позиция трека и момент её приёма */
  private clockBase = 0;
  private clockBaseAt = 0;
  private clockPlaying = false;
  private clockDuration = 0;
  private clockTrackId = "";
  /** rAF-цикл локальных часов */
  private rafId: number | null = null;
  private lastTickAt = 0;

  /** Пользователь тащит ползунок */
  private isDragging = false;
  /** Ожидаемая после перемотки позиция (сек), пока ТВ не подтвердил */
  private pendingSeekTime: number | null = null;
  /** До этого момента игнорируем входящие статусы после перемотки */
  private graceUntil = 0;

  $refs!: { rootEl: HTMLDivElement };

  mounted(): void {
    this.$refs.rootEl?.addEventListener("touchstart", this.onTouchStart, {
      passive: true,
    });
    this.$refs.rootEl?.addEventListener("touchend", this.onTouchEnd, {
      passive: true,
    });
    this.$refs.rootEl?.addEventListener("touchcancel", this.onTouchEnd, {
      passive: true,
    });
    // Часы на requestAnimationFrame: ползунок обновляется часто и идёт
    // плавно синхронно с полосой прогресса (у полосы transition отключён)
    this.startClock();
    this.applyPlayback(this.tvPlayback);
  }

  beforeUnmount(): void {
    this.$refs.rootEl?.removeEventListener("touchstart", this.onTouchStart);
    this.$refs.rootEl?.removeEventListener("touchend", this.onTouchEnd);
    this.$refs.rootEl?.removeEventListener("touchcancel", this.onTouchEnd);
    this.stopClock();
  }

  onTouchStart = (e: TouchEvent): void => {
    const el = (e.target as HTMLElement | null)?.closest("button");
    if (el) {
      this.pressedButton = el;
      el.classList.add("is-pressed");
    }
  };

  onTouchEnd = (): void => {
    if (this.pressedButton) {
      this.pressedButton.classList.remove("is-pressed");
      this.pressedButton = null;
    }
  };

  get activeCinema(): VRCinemaStatus | null {
    const movies: VRCinemaStatus[] = useVRoom().getVroomCinema;
    const movie = movies.find((m) => m.active);
    return movie || null;
  }

  get activeAudiobooks(): VRAudiobooksStatus | null {
    const books: VRAudiobooksStatus[] = useVRoom().getVroomAudiobooks;
    const book = books.find((m) => m.active);
    return book || null;
  }

  get vRoom() {
    return useVRoom();
  }

  get tvPlayback(): TVPlaybackStatus | null {
    return useTVConnect().getTvPlayback;
  }

  get playbackLabel(): string {
    const p = this.tvPlayback;
    if (!p || !p.duration) return "";
    const time = `${this.formatTime(this.clockTime)} / ${this.formatTime(p.duration)}`;
    return p.name ? `${p.name} — ${time}` : time;
  }

  /** Идёт перемотка или ждём подтверждения от ТВ — входящие статусы не применяем */
  get isSeekPending(): boolean {
    return (
      this.isDragging || this.pendingSeekTime !== null || Date.now() < this.graceUntil
    );
  }

  percentOf(seconds: number): number {
    const duration = this.tvPlayback?.duration || this.clockDuration;
    if (!duration) return 0;
    return Math.max(0, Math.min(100, (100 * seconds) / duration));
  }

  /**
   * Часы на requestAnimationFrame: между пакетами tv-status время идёт
   * само (1x) с частотой ~30 к/с, поэтому секунды не замирают, не прыгают
   * из-за джиттера сети, а ползунок движется плавно вместе с полосой.
   * Без свежих статусов останавливаемся не дальше MAX_DRIFT_SEC.
   */
  private startClock(): void {
    const loop = (): void => {
      const now = Date.now();
      if (now - this.lastTickAt >= CLOCK_MIN_STEP_MS) {
        this.lastTickAt = now;
        this.tickClock();
      }
      this.rafId = requestAnimationFrame(loop);
    };
    this.rafId = requestAnimationFrame(loop);
  }

  private stopClock(): void {
    if (this.rafId !== null) cancelAnimationFrame(this.rafId);
    this.rafId = null;
  }

  tickClock = (): void => {
    if (this.pendingSeekTime !== null && Date.now() >= this.graceUntil) {
      // ТВ не подтвердил перемотку за окно ожидания — отпускаем слайдер
      this.pendingSeekTime = null;
    }
    if (this.isDragging) return;

    const p = this.tvPlayback;
    if (!p || !p.duration) return;

    if (this.pendingSeekTime !== null) {
      // Держим позицию, куда перемотали, до подтверждения от ТВ
      this.clockTime = Math.min(this.pendingSeekTime, p.duration);
      this.rangeValue = this.percentOf(this.clockTime);
      return;
    }
    if (Date.now() < this.graceUntil || !this.clockPlaying) return;

    const elapsedSec = Math.min((Date.now() - this.clockBaseAt) / 1000, MAX_DRIFT_SEC);
    this.clockTime = Math.min(this.clockBase + elapsedSec, p.duration);
    this.rangeValue = this.percentOf(this.clockTime);
  };

  /**
   * Применяем входящий статус ТВ к локальным часам.
   * Устаревшие/внеочередные пакеты (секунды "из прошлого") пропускаем —
   * из-за них время на пульте скакало назад.
   */
  applyPlayback(p: TVPlaybackStatus | null): void {
    const now = Date.now();

    if (!p || !p.duration) {
      // Статуса нет — воспроизведение не идёт, показываем "play"
      this.play = false;
      this.clockPlaying = false;
      this.clockDuration = 0;
      this.clockTrackId = "";
      this.clockBase = 0;
      this.clockBaseAt = now;
      this.clockTime = 0;
      this.pendingSeekTime = null;
      this.graceUntil = 0;
      return;
    }

    const trackChanged = p.trackId !== this.clockTrackId;
    // Кнопка play/pause следует за реальным состоянием воспроизведения
    this.play = p.isPlaying;
    if (trackChanged) {
      // Новый трек/глава — прежние ожидания перемотки неактуальны
      this.pendingSeekTime = null;
      this.graceUntil = 0;
    }
    this.clockTrackId = p.trackId;
    this.clockDuration = p.duration;

    const nearPendingSeek =
      this.pendingSeekTime !== null &&
      Math.abs(p.currentTime - this.pendingSeekTime) <= SEEK_CONFIRM_SEC;

    if (this.pendingSeekTime !== null && !nearPendingSeek) {
      // Ждём подтверждения перемотки от ТВ — прочие пакеты несут позиции
      // до перемотки (ещё летят по сети), пропускаем их
      return;
    }
    if (nearPendingSeek) {
      // ТВ подтвердил перемотку — возобновляем следование за статусом
      this.pendingSeekTime = null;
      this.graceUntil = 0;
    }

    // Устаревший/внеочередной пакет: локальные часы уже прошли эти
    // секунды — пропускаем, иначе время на пульте откатывается назад
    if (
      p.isPlaying &&
      this.clockPlaying &&
      !trackChanged &&
      p.currentTime < this.clockTime - STALE_TOLERANCE_SEC
    ) {
      return;
    }

    const wasPlaying = this.clockPlaying;
    this.clockPlaying = p.isPlaying;
    this.clockBase = p.currentTime;
    // Не позволяем отображаемым секундам шагнуть назад из-за задержки пакета
    const aheadMs = Math.max(0, this.clockTime - p.currentTime) * 1000;
    this.clockBaseAt = now - Math.min(aheadMs, MAX_DRIFT_SEC * 1000);

    if (!p.isPlaying) {
      this.clockTime = p.currentTime;
      if (!this.isDragging && !this.isSeekPending) {
        this.rangeValue = this.percentOf(p.currentTime);
      }
      return;
    }
    // Возобновление после паузы — продолжаем с позиции ТВ
    if (!wasPlaying) this.clockTime = p.currentTime;
  }

  onDragStart(): void {
    this.isDragging = true;
  }

  onDragEnd(): void {
    this.isDragging = false;
  }

  /** Ползунок отпустили — перемотка уходит на ТВ одной командой */
  onRangeCommit(percent: number): void {
    this.isDragging = false;
    this.updateVRoomStatus(percent);
  }

  /**
   * Запомнить ожидаемую после перемотки позицию и держать слайдер,
   * пока ТВ не пришлёт статус с новой позицией (или не истечёт окно).
   */
  expectSeek(targetSeconds: number): void {
    const duration = this.tvPlayback?.duration || this.clockDuration;
    const target = Math.max(
      0,
      duration ? Math.min(targetSeconds, duration) : targetSeconds
    );
    this.pendingSeekTime = target;
    this.graceUntil = Date.now() + SEEK_GRACE_MS;
    this.clockTime = target;
    this.clockBase = target;
    this.clockBaseAt = Date.now();
    this.rangeValue = this.percentOf(target);
  }

  /** Кнопки ±5с / ±10м: ждём позицию поблизости от цели */
  fastForward(delta: number): void {
    const playback = this.tvPlayback;
    if (playback?.duration) {
      const from = this.pendingSeekTime ?? this.clockTime;
      this.expectSeek(from + delta);
    }
    this.sendCommand("fastForward", delta);
  }

  /** Play/Pause: щелчок переключает кнопку локально (оптимистично),
   *  реальное состояние вернётся статусом tv-status и синхронизируется
   *  в applyPlayback */
  togglePlay(): void {
    this.play = !this.play;
    this.sendCommand("play", "");
  }

  formatTime(seconds: number): string {
    const s = Math.max(0, Math.floor(seconds));
    const m = Math.floor(s / 60);
    const r = s % 60;
    return `${String(m).padStart(2, "0")}:${String(r).padStart(2, "0")}`;
  }

  changeVolume(volume: number): void {
    this.volume = volume;
    this.$emit("changeVolume", volume);
  }

  updateVRoomStatus(progressPersend: number): void {
    // Играет аудио на ТВ — считаем секунды от длительности трека
    // (длительность фильма из vroom треку не подходит),
    // процент уходит в property
    const playback = this.tvPlayback;
    if (playback?.duration) {
      const target = Math.round((playback.duration * progressPersend) / 100);
      this.expectSeek(target);
      this.sendCommand("set_current_time", String(target), String(progressPersend));
      return;
    }

    if (!this.activeCinema) return;
    const movie: VRCinemaStatus = { ...this.activeCinema };
    movie.current_time = String(
      Math.round((parseInt(movie.duration) * progressPersend) / 100)
    );
    useVRoom().setRvoomaCinema(movie);
    // Не даём опросу vroom вернуть слайдер назад сразу после перемотки
    this.graceUntil = Date.now() + SEEK_GRACE_MS;
    // property — процент (0–100) для перемотки аудио на ТВ:
    // длительность фильма в vroom не подходит треку/главе аудиоплеера
    this.sendCommand("set_current_time", movie.current_time, String(progressPersend));
  }

  sendCommand(type: string, value: string | number | boolean, property?: string): void {
    this.$emit("movieControl", { type, value, property, command: "movie_control" });
  }
}
export default MagicRemoteMultimedia;
</script>

<style lang="scss" scoped>
@import "@/assets/sass/variables.scss";
.btn {
  width: 58px;
  height: 58px;
  border-radius: 50%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  touch-action: manipulation;
  transition: transform 0.12s cubic-bezier(0.34, 1.56, 0.64, 1),
    background-color 0.12s ease, box-shadow 0.12s ease;
}

.bi-play-fill {
  margin-left: 0.1em;
}

.btn:active,
.btn.is-pressed {
  transform: scale(0.84);
  background-color: rgba($white, 0.35) !important;
  color: $white !important;
  box-shadow: inset 0 6px 14px rgba(0, 0, 0, 0.4) !important;
}

.btn-secondary:hover {
  background-color: rgba($white, 0.1) !important;
  color: $white;
}
</style>
